import type { InteractionResolver } from "@repo/app-types/content/structured/parser";
import {
  playbookIdSchema,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import {
  gradingIdSchema,
  type GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type { UIMessage } from "ai";

/** Reads a tool part's name + output regardless of AI SDK part variant. */
export function getToolOutput(
  part: UIMessage["parts"][number],
): { toolName: string; output: unknown } | undefined {
  const toolName =
    part.type === "dynamic-tool"
      ? part.toolName
      : part.type.startsWith("tool-")
        ? part.type.slice("tool-".length)
        : undefined;

  if (toolName === undefined) {
    return undefined;
  }

  const toolPart = part as { state?: string; output?: unknown };
  if (toolPart.state !== "output-available") {
    return undefined;
  }

  return { toolName, output: toolPart.output };
}

interface ResolvedInteraction {
  gradingId: GradingId;
  playbookId: PlaybookId;
}

/**
 * Builds an {@link InteractionResolver} for the `<quote interaction_id="...">`
 * and `<customer interaction_id="...">` tags in an agent's messages by
 * collecting the customer interactions its tool calls returned: the
 * `customer_interactions` lists of the list/count tools and the `findings` of
 * the research tool, plus the `items` rows of the playbook feedback tool. The
 * tools' `interaction_id` is the grading id, and each item carries the
 * grading's playbook.
 */
export function buildInteractionResolverFromMessages(
  messages: readonly UIMessage[],
): InteractionResolver {
  const interactions = new Map<string, ResolvedInteraction>();

  for (const message of messages) {
    for (const part of message.parts) {
      const tool = getToolOutput(part);
      if (tool) {
        collectInteractions(tool.output, interactions);
      }
    }
  }

  return (interactionId) => interactions.get(interactionId);
}

function collectInteractions(
  output: unknown,
  into: Map<string, ResolvedInteraction>,
) {
  if (typeof output !== "object" || output === null) {
    return;
  }

  const items =
    "customer_interactions" in output
      ? output.customer_interactions
      : "findings" in output
        ? output.findings
        : "items" in output
          ? output.items
          : undefined;
  if (!Array.isArray(items)) {
    return;
  }

  for (const item of items) {
    if (typeof item !== "object" || item === null) {
      continue;
    }

    const interactionId =
      "interaction_id" in item && typeof item.interaction_id === "string"
        ? item.interaction_id
        : undefined;
    // The tool's `interaction_id` is the grading id.
    const gradingId = interactionId
      ? gradingIdSchema.safeParse(interactionId).data
      : undefined;
    const playbookId =
      "playbook_id" in item
        ? playbookIdSchema.safeParse(item.playbook_id).data
        : undefined;

    if (interactionId && gradingId && playbookId) {
      into.set(interactionId, { gradingId, playbookId });
    }
  }
}
